import React from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import Logo from './Logo';

const CTA: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main CTA */}
        <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-3xl p-8 lg:p-16 text-white text-center">
          <div className="max-w-4xl mx-auto">
                                    <div className="flex justify-center mb-6">
                          <button 
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                            className="hover:opacity-80 transition-opacity cursor-pointer"
                            aria-label="Go to home"
                          >
                            <Logo size="xl" variant="on-dark" />
                          </button>
                        </div>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Ready to Transform Your AWS Security?
            </h2>
            <p className="text-xl lg:text-2xl text-blue-100 mb-8 leading-relaxed">
              Join hundreds of organizations that trust Brain Station 23's AWS Security Guardian 
              to protect their cloud infrastructure with proactive, AI-powered security management.
            </p>
            
            {/* Benefits List */}
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              <div className="flex items-center justify-center space-x-3">
                <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
                <span className="text-lg">14-day free trial</span>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
                <span className="text-lg">No credit card required</span>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
                <span className="text-lg">Setup in under 24 hours</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2">
                <span>Start Free Security Assessment</span>
                <ArrowRight className="h-5 w-5" />
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-primary-600 transition-colors">
                Schedule Live Demo
              </button>
            </div>
          </div>
        </div>

        {/* Secondary CTAs */}
        <div className="grid md:grid-cols-2 gap-8 mt-16">
          {/* AWS Marketplace */}
          <div className="bg-gray-50 rounded-2xl p-8 text-center">
            <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-orange-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6.763 10.036c.024-.048.05-.094.08-.138L9.15 5.74c.094-.137.248-.223.415-.223.167 0 .32.086.415.223l2.306 4.158c.03.044.056.09.08.138.048.095.073.2.073.309v4.516c0 .381-.31.691-.691.691H7.454c-.381 0-.691-.31-.691-.691v-4.516c0-.109.025-.214.073-.309zm7.781 3.273c0 .381.31.691.691.691h2.765c.381 0 .691-.31.691-.691V8.793c0-.109-.025-.214-.073-.309-.024-.048-.05-.094-.08-.138L16.232 4.188c-.094-.137-.248-.223-.415-.223-.167 0-.32.086-.415.223L13.096 8.346c-.03.044-.056.09-.08.138-.048.095-.073.2-.073.309v4.516z"/>
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Available on AWS Marketplace
            </h3>
            <p className="text-gray-600 mb-6">
              Deploy directly from AWS Marketplace with one-click installation 
              and seamless billing integration.
            </p>
            <button className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors">
              View on AWS Marketplace
            </button>
          </div>

          {/* Partner Program */}
          <div className="bg-gray-50 rounded-2xl p-8 text-center">
            <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Partner with Us
            </h3>
            <p className="text-gray-600 mb-6">
              Join our partner program and help your clients achieve better 
              AWS security with our comprehensive platform.
            </p>
            <button className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors">
              Become a Partner
            </button>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 text-center">
          <p className="text-gray-500 mb-8">Brain Station 23 - Trusted by security teams worldwide</p>
          <div className="flex flex-wrap justify-center items-center gap-6 opacity-60">
            <div className="bg-gray-100 px-6 py-3 rounded-lg">
              <span className="font-semibold text-gray-700">15+ Years Excellence</span>
            </div>
            <div className="bg-gray-100 px-6 py-3 rounded-lg">
              <span className="font-semibold text-gray-700">SOC 2 Type II Certified</span>
            </div>
            <div className="bg-gray-100 px-6 py-3 rounded-lg">
              <span className="font-semibold text-gray-700">AWS Advanced Technology Partner</span>
            </div>
            <div className="bg-gray-100 px-6 py-3 rounded-lg">
              <span className="font-semibold text-gray-700">500+ Expert Engineers</span>
            </div>
            <div className="bg-gray-100 px-6 py-3 rounded-lg">
              <span className="font-semibold text-gray-700">Enterprise Grade Security</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTA;
